import { ConditionNode } from "modules/record/record-producer/types/query.types";
import { getOperatorConfig, isDynamicValue, isFieldComparisonOperator, toDynamicOperator, toStaticOperator } from "./utils/operators.config";
import ReferencePathFieldSelector from "./ReferencePathFieldSelector";
import FieldSelector from "./FieldSelector";
import { OperatorSelector } from "./OperatorSelector";
import { ValueField } from "./ValueField";
import { AddConditionActions } from "./AddConditionActions";
import SysColumn from "app/class/sys-column";
import SysTable from "app/class/sys-table";
import { resolveColumnForFieldPath } from "./referencePath";

import { Stack, IconButton, Tooltip, Box, Typography, MenuItem, TextField } from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useTranslation } from "react-i18next";
import { ReactNode, useState } from "react";

interface Props {
  node: ConditionNode;
  columns: SysColumn[];
  onChange: (node: ConditionNode) => void;
  onRemove: () => void;
  onAddLogicalGroup?: (operator: 'and' | 'or') => void;
  onAddExists?: (operator?: 'and' | 'or') => void;
  valueColumns?: SysColumn[];
  valueAllColumns?: SysColumn[];
  valueTables?: SysTable[];
  allColumns?: SysColumn[];
  tables?: SysTable[];
  maxReferenceDepth?: number;
  includeFieldOperators?: boolean;
  formFields?: { name: string; label: string }[];
  flatFields?: boolean;
  compact?: boolean;
  fieldAriaLabel?: string;
  presentation?: ConditionPresentation;
  guided?: boolean;
  conditionCount?: number;
  allowedOperators?: string[];
}

export interface ConditionPresentation {
  fieldLabel: string;
  operatorLabel: string;
  valueLabel: string;
  columnGroups?: Record<string, { group: string; label: string }>;
  operatorLabelFor?: (label: string) => string | undefined;
  dynamicValueColumns?: SysColumn[];
  fixedValueLabel?: string;
  dataValueLabel?: string;
  // Hosts with their own data catalog (Fluss DataPicker) replace the flat field/data selectors.
  renderFieldPicker?: (props: { value: string; onChange: (field: string) => void; ariaLabel?: string }) => ReactNode;
  renderDataValuePicker?: (props: { value: string; onChange: (value: string) => void; ariaLabel?: string; column?: SysColumn }) => ReactNode;
  resolveColumn?: (field: string) => SysColumn | undefined;
}

export const ConditionRow = ({
  node,
  columns,
  onChange,
  onRemove,
  onAddLogicalGroup,
  onAddExists,
  valueColumns,
  valueAllColumns,
  valueTables,
  allColumns,
  tables,
  maxReferenceDepth = 5,
  includeFieldOperators = false,
  formFields,
  flatFields = false,
  compact = false,
  fieldAriaLabel,
  presentation,
  guided = false,
  conditionCount = 0,
  allowedOperators,
}: Props) => {
  const { t } = useTranslation();
  const [dataMode, setDataMode] = useState(typeof node.value === "string" && /^(record|var|payload)\./.test(node.value));
  const column = (flatFields ? (presentation?.resolveColumn?.(node.field) || columns.find((candidate) => candidate.name === node.field)) : undefined) ||
    resolveColumnForFieldPath(node.field, columns, allColumns || columns, tables || []) ||
    columns.find((c) => c.name === node.field);
  const fieldPathDepth = String(node.field || "").split(".").filter(Boolean).length;
  const hasReferencePath = !flatFields && fieldPathDepth > 1;

  const operatorConfig = column
    ? getOperatorConfig(column.column_type || "TEXT", node.operator)
    : null;

  const shouldPreserveValueOnOperatorChange = (nextOperator: string) => {
    if (!nextOperator || !column) {
      return false;
    }

    const currentUsesFieldValue = isFieldComparisonOperator(node.operator);
    const nextUsesFieldValue = isFieldComparisonOperator(nextOperator);

    if (currentUsesFieldValue || nextUsesFieldValue) {
      return currentUsesFieldValue && nextUsesFieldValue;
    }

    const currentOperatorConfig = getOperatorConfig(column.column_type || "TEXT", node.operator);
    const nextOperatorConfig = getOperatorConfig(column.column_type || "TEXT", nextOperator);

    return currentOperatorConfig?.hasValue !== false && nextOperatorConfig?.hasValue === true;
  };

  const valueForOperatorChange = (nextOperator: string) => {
    if (nextOperator === "relative_last" || nextOperator === "relative_next") {
      return node.operator === nextOperator && node.value && typeof node.value === "object"
        ? node.value : { amount: 1, unit: column?.column_type === "DATETIME" ? "minute" : "day" };
    }
    if (node.operator === "relative_last" || node.operator === "relative_next") return undefined;
    if (shouldPreserveValueOnOperatorChange(nextOperator)) {
      return node.value;
    }

    return undefined;
  };

  return (
    <Stack
      className="fluss-condition-row"
      direction={presentation || hasReferencePath ? "column" : { xs: "column", sm: "row" }}
      spacing={1}
      alignItems={presentation || hasReferencePath ? "stretch" : { xs: "stretch", sm: "center" }}
      sx={{
        width: "100%",
        minWidth: 0,
        flexWrap: { xs: "nowrap", sm: "wrap", lg: "nowrap" },
        p: { xs: 1, sm: 0 },
        border: { xs: "1px solid", sm: "none" },
        borderColor: { xs: "divider", sm: "transparent" },
        borderRadius: { xs: 1, sm: 0 },
      }}
    >
      <Stack
        direction={presentation ? "column" : { xs: "column", sm: "row" }}
        spacing={1}
        alignItems={presentation ? "stretch" : { xs: "stretch", sm: hasReferencePath ? "flex-end" : "center" }}
        sx={{
          width: "100%",
          minWidth: 0,
          flex: "1 1 auto",
          flexWrap: { xs: "nowrap", sm: "wrap", lg: "nowrap" },
        }}
      >
        <Box sx={presentation ? { width: "100%", minWidth: 0 } : { width: { xs: "100%", sm: "auto" }, minWidth: { xs: 0, sm: 180 }, flex: { xs: "1 1 100%", sm: "1 1 220px", lg: "1 1 220px" } }}>
          {presentation && <Typography variant="caption" display="block">{presentation.fieldLabel}</Typography>}
          {flatFields && presentation?.renderFieldPicker ? (
            presentation.renderFieldPicker({
              value: node.field,
              ariaLabel: fieldAriaLabel,
              onChange: (field) => onChange({ ...node, field, operator: "", value: undefined }),
            })
          ) : flatFields ? (
            <FieldSelector
              value={node.field}
              columns={columns}
              ariaLabel={fieldAriaLabel}
              columnGroups={presentation?.columnGroups}
              onChange={(field) =>
                onChange({
                  ...node,
                  field,
                  operator: "",
                  value: undefined,
                })
              }
            />
          ) : (
            <ReferencePathFieldSelector
              value={node.field}
              columns={columns}
              allColumns={allColumns || columns}
              tables={tables || []}
              maxDepth={maxReferenceDepth}
              ariaLabel={guided ? String(t("Field")) : undefined}
              placeholder={guided ? String(t("Select a field")) : undefined}
              onChange={(field) =>
                onChange({
                  ...node,
                  field,
                  operator: "",
                  value: undefined,
                })
              }
            />
          )}
        </Box>

        <Box sx={presentation ? { width: "100%", minWidth: 0 } : { width: { xs: "100%", sm: "auto" }, minWidth: { xs: 0, sm: 160 }, flex: { xs: "1 1 100%", sm: "1 1 180px", lg: "0 1 190px" } }}>
          {presentation && <Typography variant="caption" display="block">{presentation.operatorLabel}</Typography>}
          <OperatorSelector
            column={column}
            value={node.operator}
            includeFieldOperators={includeFieldOperators}
            labelFor={presentation?.operatorLabelFor}
            ariaLabel={guided ? String(t("Operator")) : undefined}
            placeholder={guided ? String(t("Operator")) : undefined}
            allowedOperators={allowedOperators}
            onChange={(operator) =>
              onChange({
                ...node,
                operator,
                value: valueForOperatorChange(operator),
              })
            }
          />
        </Box>

        {presentation?.dynamicValueColumns && operatorConfig?.hasValue && (
          <TextField select size="small" value={dataMode ? "data" : "fixed"}
            inputProps={{ "aria-label": "Value mode" }}
            onChange={(event) => { setDataMode(event.target.value === "data"); onChange({ ...node, value: undefined }); }}>
            <MenuItem value="fixed">{presentation.fixedValueLabel || "Fixed value"}</MenuItem>
            <MenuItem value="data">{presentation.dataValueLabel || "Data"}</MenuItem>
          </TextField>
        )}
        {presentation?.dynamicValueColumns && operatorConfig?.hasValue && dataMode && presentation.renderDataValuePicker ? (
          presentation.renderDataValuePicker({
            value: typeof node.value === "string" ? node.value : "",
            ariaLabel: presentation.valueLabel,
            column,
            onChange: (value) => onChange({ ...node, value }),
          })
        ) : presentation?.dynamicValueColumns && operatorConfig?.hasValue && dataMode ? (
          <FieldSelector value={node.value} columns={presentation.dynamicValueColumns} ariaLabel={presentation.valueLabel}
            columnGroups={presentation.columnGroups} onChange={(value) => onChange({ ...node, value })} />
        ) : isFieldComparisonOperator(node.operator) ? (
          <Box sx={presentation ? { width: "100%", minWidth: 0 } : { width: { xs: "100%", sm: "auto" }, minWidth: { xs: 0, sm: 180 }, flex: { xs: "1 1 100%", sm: "1 1 220px", lg: "1 1 240px" } }}>
            {presentation && <Typography variant="caption" display="block">{presentation.valueLabel}</Typography>}
            <ReferencePathFieldSelector
              value={String(node.value || "")}
              columns={valueColumns || columns}
              allColumns={valueAllColumns || allColumns || valueColumns || columns}
              tables={valueTables || tables || []}
              maxDepth={maxReferenceDepth}
              onChange={(value) =>
                onChange({
                  ...node,
                  value,
                })
              }
            />
          </Box>
        ) : ((operatorConfig && operatorConfig.hasValue) || (operatorConfig == null)) && (
          <Box sx={presentation ? { width: "100%", minWidth: 0 } : { width: { xs: "100%", sm: "auto" }, minWidth: { xs: 0, sm: 180 }, flex: { xs: "1 1 100%", sm: "1 1 220px", lg: "1 1 240px" } }}>
            {presentation && <Typography variant="caption" display="block">{presentation.valueLabel}</Typography>}
            <ValueField
              key={node.field}
              column={{ ...column, name: "value", default_value: "" } as SysColumn}
              value={node.value}
              onChange={(value) =>
                onChange({
                  ...node,
                  operator: presentation?.dynamicValueColumns ? node.operator : isDynamicValue(value)
                    ? toDynamicOperator(node.operator)
                    : toStaticOperator(node.operator),
                  value,
                })
              }
              operator={node.operator}
              columns={columns}
              allColumns={allColumns || columns}
              tables={tables || []}
              maxReferenceDepth={maxReferenceDepth}
              formFields={formFields}
              ariaLabel={guided ? String(t("Value")) : undefined}
              placeholder={guided ? String(t("Enter a value")) : undefined}
            />
          </Box>
        )}

        {!compact && (
          <AddConditionActions
            onAddLogicalGroup={onAddLogicalGroup}
            onAddExists={onAddExists}
            useGenericAdd={guided && conditionCount < 2}
          />
        )}

        <Tooltip title={t("Remove condition")}>
          <IconButton
            aria-label={String(t("Remove condition"))}
            size="small"
            color="error"
            onClick={onRemove}
            sx={{
              alignSelf: { xs: "flex-end", sm: "center" },
              width: { xs: 40, sm: 32 },
              height: { xs: 40, sm: 32 },
              flexShrink: 0,
            }}
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
    </Stack>
  );
};
